import { Component, OnInit, Input, Output, EventEmitter, Injectable, OnDestroy, OnChanges, SimpleChanges, inject } from '@angular/core';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { delay } from 'rxjs/operators';
import { WorksheetService } from '@fgb/core';
import { Playable } from 'src/app/shared/directives/playable.directive';
declare var YT: any;
declare var gtag: any;

@Injectable({ providedIn: 'root' })
export class YoutubeService {
  private static behaviourSubject: BehaviorSubject<boolean>;
  private static scriptLoaded = false;

  constructor() { }

  addVideo(): Observable<boolean> {
    if (!YoutubeService.behaviourSubject) {
      YoutubeService.behaviourSubject = new BehaviorSubject<boolean>(false);

      // Only load script if not already loaded
      if (!YoutubeService.scriptLoaded) {
        YoutubeService.scriptLoaded = true;

        // Create the script tag
        const tag = document.createElement('script');
        tag.src = 'https://www.youtube.com/iframe_api';

        // Set up the callback
        (window as any).onYouTubeIframeAPIReady = () => {
          YoutubeService.behaviourSubject.next(true);
        };

        // Insert the script tag
        const firstScriptTag = document.getElementsByTagName('script')[0];
        firstScriptTag.parentNode!.insertBefore(tag, firstScriptTag);
      } else if ((window as any).YT) {
        // If script is already loaded but BehaviorSubject wasn't created yet
        YoutubeService.behaviourSubject.next(true);
      }
    }

    return YoutubeService.behaviourSubject.asObservable();
  }
}

@Component({
  selector: 'fgb-youtube-player',
  templateUrl: './fgb-youtube-player.component.html',
  styleUrls: ['./fgb-youtube-player.component.scss'],
})
export class FgbYoutubePlayerComponent extends Playable implements OnInit, OnChanges, OnDestroy {
  @Input() streamKey: string;
  @Output() onStateChange = new EventEmitter();

  private subscription: Subscription;
  private player: any;
  private progressCheckInterval: any;
  private eightyPercentWatched = false;
  public static analyticsInterval = 0;

  private youtubeService = inject(YoutubeService);
  private worksheetService = inject(WorksheetService);

  public play(): void {
    if (this.player && this.player.playVideo) {
      this.player.playVideo();
    }
  }

  public pause(): void {
    if (this.player && this.player.pauseVideo) {
      this.player.pauseVideo();
    }
  }

  public stop(): void {
    if (this.player && this.player.stopVideo) {
      this.player.stopVideo();
    }
  }

  ngOnInit() {
    this.subscription = this.youtubeService
      .addVideo()
      .pipe(delay(100))
      .subscribe((youtubeStatus) => {
        if (youtubeStatus == true) {
          this.initializePlayer();
        }
      });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.streamKey && !changes.streamKey.firstChange) {
      this.updatePlayer();
    }
  }

  private updatePlayer() {
    if (this.player) {
      // Clear existing intervals
      this.clearIntervals();

      // Load new video if player exists
      this.player.loadVideoById(this.streamKey);
    } else {
      // If player doesn't exist yet, initialize it
      this.initializePlayer();
    }
  }

  private initializePlayer() {
    if (!(window as any).YT) {
      // Return false if youTube API not loaded yet
      return;
    }

    // Destroy existing player if it exists
    if (this.player && this.player.destroy) {
      this.player.destroy();
    }

    try {
      this.player = new (window as any).YT.Player('ytplayer_' + this.streamKey, {
        aspectRatio: 16 / 9,
        width: '100%',
        height: '100%',
        videoId: this.streamKey,
        events: {
          onStateChange: (event: any) => this.handleStateChange(event),
        },
      });
    } catch (err) {
      console.error('Failed to initialize YouTube player', err);
    }
  }

  private handleStateChange(event: any) {
    this.onStateChange.emit(event);

    if (event.data == YT.PlayerState.PLAYING) {
      // Video started playing
      this.startProgressTracking();
      this.startAnalyticsInterval();

      gtag('event', 'streaming', {
        streamKey: this.streamKey,
        event_category: 'start_stream',
        event_label: this.streamKey,
      });
    } else if (event.data == YT.PlayerState.PAUSED) {
      // Video paused
      gtag('event', 'streaming', {
        streamKey: this.streamKey,
        event_category: 'pause_stream',
        event_label: this.streamKey,
      });
      this.clearIntervals();
    } else if (event.data == YT.PlayerState.ENDED) {
      // Video ended
      this.clearIntervals();
    }
  }

  private startProgressTracking() {
    this.eightyPercentWatched = false;
    this.progressCheckInterval = setInterval(() => {
      try {
        const duration = this.player.getDuration();
        const currentTime = this.player.getCurrentTime();
        const progress = (currentTime / duration) * 100;

        if (progress >= 80 && !this.eightyPercentWatched) {
          this.eightyPercentWatched = true;
          this.handleEightyPercentWatched();
        }
      } catch (err) {
        this.clearIntervals();
      }
    }, 1000); // Check every second
  }

  private handleEightyPercentWatched() {
    // Sending the POST request
    this.worksheetService.logStreamWatching(this.streamKey);

    gtag('event', 'streaming', {
      streamKey: this.streamKey,
      event_category: '80_percent_watched',
      event_label: this.streamKey,
    });
  }

  private startAnalyticsInterval() {
    FgbYoutubePlayerComponent.analyticsInterval = window.setInterval(() => {
      gtag('event', 'streaming', {
        streamKey: this.streamKey,
        event_category: 'watching_stream',
        event_label: this.streamKey,
      });
    }, 30000);
  }

  private clearIntervals() {
    window.clearInterval(FgbYoutubePlayerComponent.analyticsInterval);
    window.clearInterval(this.progressCheckInterval);
  }

  ngOnDestroy() {
    this.clearIntervals();
    this.subscription.unsubscribe();
    if (this.player && this.player.destroy) {
      this.player.destroy();
    }
  }
}
